import moment from "moment";
import { formatDateTime } from "./dateTime";
import { getExpenseTypeName } from "./ExpenseConverter";
import {
  getLoanTypeLabel,
  getLoanStatusLabel,
} from "./LoanConverter";

/** Column ids excluded from table search (numeric / summary fields). */
const NON_SEARCHABLE_COLUMN_IDS = new Set([
  "actions",
  "payments",
  "customer_payment_actions",
  "company_invoice_actions",
  "change_password",
  "actions_invoice_delete",
  "actions_stock_draft",
  "total",
  "paid",
  "payable",
  "received",
  "receivable",
  "recieved",
  "recieveable",
  "qty",
  "in_qty",
  "out_qty",
  "in-qty",
  "out-qty",
  "opening_qty",
]);

const NESTED_NAME_FIELDS = new Set([
  "category_id",
  "companyId",
  "customerId",
  "categoryId",
  "subcategoryId",
  "supplierId",
  "itemId",
  "catId",
]);

function normalizeId(id) {
  return String(id ?? "")
    .toLowerCase()
    .replace(/-/g, "_");
}

export function isNonSearchableColumn(column) {
  if (!column?.id) return true;
  const id = column.id;
  const normalized = normalizeId(id);
  return (
    NON_SEARCHABLE_COLUMN_IDS.has(id) ||
    NON_SEARCHABLE_COLUMN_IDS.has(normalized)
  );
}

function nestedDisplayValue(value) {
  if (value == null) return "";
  if (typeof value === "object") {
    if (value.name != null) return String(value.name);
    if (value.title != null) return String(value.title);
    if (value.code != null) return String(value.code);
    return "";
  }
  return String(value);
}

/**
 * Text used for search — aligned with TableComp display where practical.
 */
export function getTableCellSearchText(row, column) {
  if (!row || !column?.id || isNonSearchableColumn(column)) return "";

  const id = column.id;

  if (NESTED_NAME_FIELDS.has(id)) {
    return nestedDisplayValue(row[id]);
  }

  if (id === "payment_type") {
    return row.payment_type === 1 ? "cash" : "bank";
  }

  if (id === "type") {
    const asText = getExpenseTypeName(row);
    return asText ? String(asText).toLowerCase() : "";
  }

  if (id === "loanType") {
    const asText = getLoanTypeLabel(row);
    return asText ? String(asText).toLowerCase() : "";
  }

  if (id === "status") {
    const asText = getLoanStatusLabel(row);
    return asText ? String(asText).toLowerCase() : "";
  }

  if (id === "partyName") {
    return String(row.partyName || "").toLowerCase();
  }

  if (id === "user_type" || id === "userType") {
    const t = row.user_type ?? row.userType;
    return t === 1 ? "customer" : "company";
  }

  if (id === "date") {
    const raw = row.date;
    if (column.type === "stock-stats") return String(raw ?? "");
    if (typeof raw === "number") {
      return formatDateTime(raw);
    }
    if (raw) {
      try {
        return formatDateTime(raw);
      } catch {
        return String(raw);
      }
    }
    return "";
  }

  const value = row[id];
  if (value == null) return "";
  if (typeof value === "object") return nestedDisplayValue(value);
  return String(value);
}

/**
 * Filter table rows: search text matches any searchable column (case-insensitive contains).
 */
export function filterTableRowsBySearch(rows, searchText, columns) {
  if (!Array.isArray(rows)) return [];
  const q = String(searchText ?? "").trim().toLowerCase();
  if (!q) return rows;

  const searchableColumns = (Array.isArray(columns) ? columns : []).filter(
    (col) => !isNonSearchableColumn(col)
  );

  if (searchableColumns.length === 0) return rows;

  return rows.filter((row) =>
    searchableColumns.some((col) => {
      const text = getTableCellSearchText(row, col).toLowerCase();
      return text && text.includes(q);
    })
  );
}
